import { useState, type FormEvent } from "react";
import type { ActorDTO, RunDTO } from "@roomate/core";
import { useAbandonRun } from "../api.ts";
import { Action } from "../calm.tsx";
import { ErrorNote, inputCls } from "../ui.tsx";
import { Dialog, Field } from "../pages/Project.tsx";

export const ABANDON_REASON_MIN = 10;
export const ABANDON_REASON_MAX = 4000;

/** Admin-only, and only for a run that is still live (the hub answers 409 run_finished otherwise). */
export function canAbandonRun(me: Pick<ActorDTO, "kind" | "access">, run: Pick<RunDTO, "status">): boolean {
  return me.kind === "human" && me.access === "admin" && (run.status === "starting" || run.status === "running");
}

export function abandonReasonValid(reason: string): boolean {
  const n = reason.trim().length;
  return n >= ABANDON_REASON_MIN && n <= ABANDON_REASON_MAX;
}

export function AbandonRunButton({ visible, onClick }: { visible: boolean; onClick: () => void }) {
  return visible ? (
    <Action danger onClick={onClick} testId="run-abandon">
      Abandon run
    </Action>
  ) : null;
}

export function AbandonRunForm({
  reason,
  pending,
  error,
  onReasonChange,
  onCancel,
  onSubmit,
}: {
  reason: string;
  pending: boolean;
  error: unknown;
  onReasonChange: (reason: string) => void;
  onCancel: () => void;
  onSubmit: (event: FormEvent) => void;
}) {
  return (
    <form onSubmit={onSubmit} className="space-y-5" data-testid="run-abandon-form">
      <p className="text-[13.5px] leading-relaxed text-ink-2">This closes the run as stopped without waiting for its worker. The reason is kept on the run.</p>
      <Field label="Reason" hint={`at least ${ABANDON_REASON_MIN} characters`}>
        <textarea
          className={inputCls}
          rows={3}
          value={reason}
          onChange={(event) => onReasonChange(event.target.value)}
          minLength={ABANDON_REASON_MIN}
          maxLength={ABANDON_REASON_MAX}
          required
          autoFocus
          data-testid="run-abandon-reason"
        />
      </Field>
      <ErrorNote error={error} />
      <div className="flex justify-end gap-2">
        <Action onClick={onCancel} disabled={pending} testId="run-abandon-cancel">
          Keep run
        </Action>
        <Action danger type="submit" disabled={!abandonReasonValid(reason) || pending} testId="run-abandon-confirm">
          Abandon run
        </Action>
      </div>
    </form>
  );
}

/** The button plus its reason dialog. Renders nothing unless the viewer may abandon this run. */
export function AbandonRunControl({ me, run }: { me: Pick<ActorDTO, "kind" | "access">; run: RunDTO }) {
  const abandon = useAbandonRun();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  if (!canAbandonRun(me, run)) return null;
  const reset = () => {
    setOpen(false);
    setReason("");
    abandon.reset();
  };
  // While the POST is in flight, closing would look like a cancel but the run still gets abandoned.
  const close = () => {
    if (!abandon.isPending) reset();
  };
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!abandonReasonValid(reason)) return;
    abandon.mutate({ id: run.id, reason: reason.trim() }, { onSuccess: reset });
  };
  return (
    <>
      <AbandonRunButton visible onClick={() => setOpen(true)} />
      {open && (
        <Dialog title="Abandon run" onClose={close}>
          <AbandonRunForm reason={reason} pending={abandon.isPending} error={abandon.error} onReasonChange={setReason} onCancel={close} onSubmit={submit} />
        </Dialog>
      )}
    </>
  );
}
